import { GroupCondition } from '@/api/enums/conditions.enum';
import { equal, handleAxiosError } from '@/api/helpers';
import { group } from '@/api/helpers/filter.helper';
import { Quiz } from '@/client/quizzes';
import { Actions, Subjects } from '@/client/users';
import { RedirectPaths, RedirectPathsEnum } from '@/common/constants';
import { SECURITY_CULTURE_BOOK_DEMO } from '@/common/constants/external-urls';
import { ViewOnlyRestrictionWhen } from '@/components/custom-roles';
import { SurveysDatatableV2 } from '@/components/security-culture/datatables/SurveysDatatableV2';
import { SurveyPreviewModal } from '@/components/security-culture/modals/SurveyPreviewModal';
import {
  AutoScheduleBadge,
  AutoScheduleDescription,
  AutoScheduleLabel,
  PlannerCardTop,
} from '@/components/security-culture/PlannerCard.styled';
import { SurveyModal } from '@/components/security-culture/SurveyModal';
import {
  useCloneQuiz,
  useDeleteQuiz,
  useQuizzes,
} from '@/hooks/query/quizzes.hooks';
import { useAppSelector } from '@/hooks/store';
import { usePermission } from '@/hooks/usePermission';
import { useScAutoSchedule } from '@/hooks/useScAutoSchedule';
import { useToast } from '@/hooks/useToast';
import { selectCurrentAccount } from '@/store/features/account';
import { selectCurrentUser } from '@/store/features/users/current-user.slice';
import { AppButton } from '@/ui/buttons';
import { FlexContainer } from '@/ui/styled-ui';
import { AppSwitch } from '@/ui/switch/AppSwitch';
import {
  ComputerIconPath,
  SCEditIcon,
  SurveySendIconPath,
  SurveyTemplateIcon,
} from '@/utils/helpers';
import { AxiosError } from 'axios';
import { noop } from 'lodash';
import { Tooltip } from 'primereact/tooltip';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const PlannerCombinedCard = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  margin-bottom: var(--semi-big-padding);
`;

const PlannerCardBottom = styled.div`
  background-color: var(--beige-main);
  border: 1px solid var(--gray-moderate);
  border-radius: 0 0 var(--semi-medium-border-radius)
    var(--semi-medium-border-radius);
  padding: var(--xlarge-padding);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 52px;
  width: 100%;
`;

const SendMailIconWrapper = styled.div`
  opacity: 0.4;
  width: 72px;
  height: 72px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--black-main);
`;

const SendCTATitle = styled.h3`
  font-size: var(--modal-heading-font-size);
  font-weight: 700;
  margin: 0;
  text-align: center;
  color: var(--black-main);
`;

const SendCTADescription = styled.p`
  font-size: var(--medium-font-size);
  line-height: 1.4;
  margin: 0;
  text-align: center;
  color: var(--black-main);
  max-width: 50%;
`;

const TemplateCard = styled.div`
  background-color: var(--beige-main);
  border-radius: var(--medium-border-radius);
  padding: var(--default-padding);
  display: flex;
  align-items: center;
  gap: var(--default-padding-small);
  width: 100%;
  margin-top: var(--semi-big-padding);
`;

const TemplateIconWrapper = styled.div`
  width: 55px;
  height: 55px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--security-culture-primary, var(--purple-text));
`;

const TemplateTitle = styled.span`
  font-size: var(--medium-font-size);
  font-weight: 600;
  color: var(--black-main);
`;

const TemplateDescription = styled.p`
  font-size: var(--small-font-size);
  color: var(--main-text-color);
  margin: 0;
`;

type SCPlannerTableViewProps = {
  baselineUsed?: boolean;
};

export const SCPlannerTableView: React.FC<SCPlannerTableViewProps> = ({
  baselineUsed = false,
}) => {
  const { t } = useTranslation();
  const { isRestrictedByRole } = usePermission();
  const isSurveyRestricted = isRestrictedByRole(
    Actions.CREATE,
    Subjects.SECURITY_CULTURE,
  );
  const navigate = useNavigate();
  const toast = useToast();
  const currentAccount = useAppSelector(selectCurrentAccount);
  const currentUser = useAppSelector(selectCurrentUser);

  const { autoScheduleEnabled, toggleAutoSchedule } = useScAutoSchedule();

  const [previewQuizId, setPreviewQuizId] = useState<string | undefined>(
    undefined,
  );
  const [surveyModal, setSurveyModal] = useState<boolean>(false);
  const [currentSurvey, setCurrentSurvey] = useState<Quiz | undefined>(
    undefined,
  );

  const { quizzes, refetch: refetchQuizzes } = useQuizzes(
    currentAccount?.id as string,
    {
      filters: [
        group(GroupCondition.OR, [
          equal('account', currentAccount?.id),
          equal('account', null),
        ]),
      ],
      enabled: !!currentAccount?.id,
    },
  );

  const masterQuiz = quizzes?.result?.find((q) => q.account === null);
  const accountCloneQuiz = quizzes?.result?.find((q) => q.account !== null);
  const hasCloneQuiz = Boolean(accountCloneQuiz);

  const cloneQuizHook = useCloneQuiz();
  const deleteQuizHook = useDeleteQuiz();

  const handleCreateCopy = async () => {
    if (!masterQuiz?.id || !currentAccount?.id) return;
    try {
      const response = await cloneQuizHook.clone({
        id: masterQuiz.id,
        account: currentAccount.id,
      });

      if (!response?.id) return;
      refetchQuizzes();
      setCurrentSurvey(response);
      toast?.success(t('toast.success'), t('sc.survey.created'));
    } catch (error) {
      handleAxiosError(error as Error | AxiosError, toast);
    } finally {
      setSurveyModal(true);
    }
  };

  const handleDeleteCopy = async () => {
    if (!accountCloneQuiz?.id || !currentAccount?.id) return;
    try {
      await deleteQuizHook.delete({
        accountId: currentAccount.id,
        quizId: accountCloneQuiz.id,
      });
      toast?.success(t('toast.success'), t('sc.survey.deleted'));
      refetchQuizzes();
    } catch (error) {
      handleAxiosError(error as Error | AxiosError, toast);
    }
  };

  return (
    <>
      <PlannerCombinedCard>
        <PlannerCardTop>
          <AutoScheduleBadge>
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="currentColor"
              xmlns="http://www.w3.org/2000/svg"
            >
              <ComputerIconPath />
            </svg>
          </AutoScheduleBadge>
          <FlexContainer
            direction="column"
            align="flex-start"
            gap={1}
            flex="1"
            width="auto"
          >
            <AutoScheduleLabel>
              {t('security.culture.planner.auto.schedule')}
              <i
                id="auto-schedule-info-icon"
                className="pi pi-info-circle"
                style={{
                  fontSize: 'var(--small-font-size)',
                  cursor: 'pointer',
                  color: 'var(--main-text-color)',
                }}
              />
            </AutoScheduleLabel>
            <Tooltip
              target="#auto-schedule-info-icon"
              position="top"
              content={t(
                'security.culture.send.survey.auto.schedule.tooltip.paying',
              )}
            />
            <AutoScheduleDescription>
              {t('security.culture.planner.auto.schedule.description')}
            </AutoScheduleDescription>
          </FlexContainer>
          {baselineUsed ? (
            <>
              <Tooltip
                target="#planner-auto-schedule-toggle"
                position="left"
                content={t(
                  'security.culture.send.survey.auto.schedule.tooltip',
                )}
              />
              <span
                id="planner-auto-schedule-toggle"
                style={{ cursor: 'pointer' }}
              >
                <AppSwitch checked={false} disabled onChange={noop} />
              </span>
            </>
          ) : (
            <ViewOnlyRestrictionWhen restricted={isSurveyRestricted}>
              <AppSwitch
                checked={autoScheduleEnabled}
                disabled={isSurveyRestricted}
                onChange={(e) =>
                  !isSurveyRestricted && toggleAutoSchedule(e.value)
                }
              />
            </ViewOnlyRestrictionWhen>
          )}
        </PlannerCardTop>

        <PlannerCardBottom>
          <FlexContainer direction="column" align="center" gap={12}>
            <SendMailIconWrapper>
              <svg
                width="72"
                height="72"
                viewBox="0 0 24 24"
                fill="currentColor"
                xmlns="http://www.w3.org/2000/svg"
              >
                <SurveySendIconPath />
              </svg>
            </SendMailIconWrapper>
            <FlexContainer direction="column" align="center" gap={10}>
              <SendCTATitle>
                {baselineUsed
                  ? t('security.culture.planner.baseline.used.heading')
                  : t('security.culture.planner.send.cta.title')}
              </SendCTATitle>
              <SendCTADescription>
                {baselineUsed
                  ? t('security.culture.planner.baseline.used.description')
                  : t('security.culture.planner.send.cta.description')}
              </SendCTADescription>
            </FlexContainer>
          </FlexContainer>
          <FlexContainer gap={14} justify="center" width="auto">
            {baselineUsed ? (
              <a
                href={SECURITY_CULTURE_BOOK_DEMO}
                target="_blank"
                rel="noopener noreferrer"
              >
                <AppButton
                  label={t('security.culture.planner.talk.to.sales')}
                  severity="success"
                />
              </a>
            ) : (
              <ViewOnlyRestrictionWhen restricted={isSurveyRestricted}>
                <AppButton
                  label={t('security.culture.planner.schedule.survey')}
                  isDisabled={isSurveyRestricted}
                  onClick={() =>
                    navigate(
                      RedirectPaths[
                        RedirectPathsEnum.SECURITY_CULTURE_SEND_SURVEY_V2
                      ](),
                    )
                  }
                />
              </ViewOnlyRestrictionWhen>
            )}
            <AppButton
              label={t('security.culture.planner.preview.survey')}
              type="outlined"
              severity="no-background"
              onClick={() =>
                setPreviewQuizId(
                  hasCloneQuiz ? accountCloneQuiz?.id : masterQuiz?.id,
                )
              }
            />
          </FlexContainer>
        </PlannerCardBottom>
      </PlannerCombinedCard>

      <SurveysDatatableV2 />

      {currentUser?.account?.isSystem && (
        <>
          <TemplateCard>
            <TemplateIconWrapper>
              <SurveyTemplateIcon />
            </TemplateIconWrapper>
            <FlexContainer
              direction="column"
              align="flex-start"
              gap={4}
              flex="1"
              width="auto"
            >
              <TemplateTitle>
                {t('security.culture.planner.template.default.title')}
              </TemplateTitle>
              <TemplateDescription>
                {t('security.culture.planner.template.default.description')}
              </TemplateDescription>
            </FlexContainer>
            <FlexContainer gap={14} align="center" width="auto">
              <AppButton
                label={t('security.culture.planner.template.preview')}
                onClick={() => setPreviewQuizId(masterQuiz?.id)}
              />
              {!hasCloneQuiz && (
                <AppButton
                  label={t('security.culture.planner.template.create.copy')}
                  type="outlined"
                  severity="no-background"
                  onClick={handleCreateCopy}
                />
              )}
            </FlexContainer>
          </TemplateCard>
          {hasCloneQuiz && (
            <TemplateCard>
              <TemplateIconWrapper>
                <SCEditIcon />
              </TemplateIconWrapper>
              <FlexContainer
                direction="column"
                align="flex-start"
                gap={4}
                flex="1"
                width="auto"
              >
                <TemplateTitle>
                  {t('security.culture.planner.template.clone.title')}
                </TemplateTitle>
                <TemplateDescription>
                  {t('security.culture.planner.template.clone.description')}
                </TemplateDescription>
              </FlexContainer>
              <FlexContainer gap={14} align="center" width="auto">
                <AppButton
                  label={t('security.culture.planner.template.preview')}
                  onClick={() => setPreviewQuizId(accountCloneQuiz?.id)}
                />
                <AppButton
                  label={t('generic.edit')}
                  type="outlined"
                  severity="no-background"
                  onClick={() => {
                    setCurrentSurvey(accountCloneQuiz);
                    setSurveyModal(true);
                  }}
                />
                <AppButton
                  label={t('security.culture.planner.template.delete.copy')}
                  type="outlined"
                  severity="no-background"
                  onClick={handleDeleteCopy}
                />
              </FlexContainer>
            </TemplateCard>
          )}
        </>
      )}

      <SurveyModal
        visible={surveyModal}
        onHide={() => {
          setSurveyModal(false);
          setCurrentSurvey(undefined);
        }}
        surveyId={currentSurvey?.id}
        refetch={() => refetchQuizzes()}
      />
      <SurveyPreviewModal
        visible={!!previewQuizId}
        onHide={() => setPreviewQuizId(undefined)}
        surveyId={previewQuizId}
      />
    </>
  );
};
